import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, liveStatusTranslationKey, notificationProvidersTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { ChannelProviderConfigDetail, ChannelProviderConfigSummary, KavenegarWebhookSetup } from './notification.models';
import { NotificationService } from './notification.service';
import { normalizeNotificationChannel, providerKeyLabel, validateSmtpSettings, validateTestRecipient } from './notification.utils';

type ProviderEditorMode = 'smtp-create' | 'smtp-edit' | 'kavenegar-create' | 'kavenegar-edit' | 'gmail' | null;

@Component({
  standalone: true,
  selector: 'ain-notifications-providers',
  imports: [
    AinPageComponent, AinStateComponent, InfoHintComponent, AinFormFieldComponent,
    FormsModule, RouterLink, TableModule, ButtonModule, DialogModule, InputTextModule, InputNumberModule, SelectModule,
    CheckboxModule, AinCanAccessDirective
  ],
  template: `
    <ain-page [eyebrow]="t('notifications.eyebrow')" [title]="t('notifications.providers.title')" [description]="t('notifications.providers.description')">
      <ain-info-hint page-title-addon [titleKey]="'notifications.providers.help.title'" [descriptionKey]="'notifications.providers.help.description'"
        [bulletKeys]="['notifications.providers.help.bullets.accounts','notifications.providers.help.bullets.health','notifications.providers.help.bullets.test']" />

      <section class="card toolbar">
        <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton type="button" [label]="t('notifications.providers.actions.addSmtp')" (click)="openSmtpCreate()"></button>
        <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton type="button" severity="secondary" [label]="t('notifications.providers.actions.addKavenegar')" (click)="openKavenegarCreate()"></button>
        <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton type="button" severity="help" [label]="t('notifications.providers.actions.connectGmail')" [loading]="gmailConnecting()" (click)="connectGmail()"></button>
        <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </section>

      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> }
      @else if (error()) { <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" /> }
      @else if (providers().length === 0) { <ain-state type="empty" [title]="t('notifications.providers.empty')" /> }
      @else {
        <section class="card">
          <p-table [value]="providers()" [paginator]="true" [rows]="12">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('notifications.providers.columns.name') }}</th>
                <th>{{ t('notifications.providers.columns.channel') }}</th>
                <th>{{ t('notifications.providers.columns.provider') }}</th>
                <th>{{ t('notifications.providers.columns.health') }}</th>
                <th>{{ t('notifications.providers.columns.enabled') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><a [routerLink]="['/notifications/providers', row.id]">{{ row.displayName }}</a></td>
                <td>{{ channelLabel(row.channel) }}</td>
                <td>{{ providerLabel(row.providerKey) }}</td>
                <td>{{ row.healthStatus }} / {{ row.readinessStatus }}</td>
                <td>{{ row.isEnabled ? t('common.yes') : t('common.no') }}</td>
                <td class="actions">
                  <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton size="small" [label]="t('common.edit')" (click)="openEdit(row)"></button>
                  <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton size="small" severity="secondary" [label]="row.isEnabled ? t('notifications.providers.actions.disable') : t('notifications.providers.actions.enable')" (click)="toggleEnabled(row)"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }

      <p-dialog [header]="dialogTitle()" [visible]="dialogVisible()" (visibleChange)="onDialogVisibleChange($event)" [modal]="true" [style]="{ width:'min(760px,94vw)' }">
        <div class="dialog-back"><a routerLink="/notifications/providers" (click)="onDialogVisibleChange(false)">{{ t('common.back') }}</a></div>
        @if (editorMode() === 'gmail') {
          <ain-info-hint [titleKey]="'notifications.providers.help.gmail.title'" [descriptionKey]="'notifications.providers.help.gmail.description'" />
          <ain-form-field [label]="t('notifications.providers.fields.testRecipient')">
            <input pInputText [(ngModel)]="testRecipient" />
          </ain-form-field>
          <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton type="button" [label]="t('notifications.providers.actions.testSend')" [loading]="testing()" (click)="testGmailSend()"></button>
          <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton type="button" severity="danger" [label]="t('notifications.providers.actions.disconnect')" (click)="disconnectGmail()"></button>
        } @else if (editorMode()?.startsWith('kavenegar')) {
          <div class="grid">
            <ain-form-field [label]="t('notifications.providers.fields.displayName')"><input pInputText [(ngModel)]="kavenegarForm.displayName" /></ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.senderLine')"><input pInputText [(ngModel)]="kavenegarForm.senderLine" /></ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.apiBaseUrl')"><input pInputText [(ngModel)]="kavenegarForm.apiBaseUrl" /></ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.apiKey')"><input pInputText type="password" [(ngModel)]="kavenegarForm.apiKey" [placeholder]="t('notifications.providers.placeholders.secretMasked')" /></ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.priority')"><p-inputNumber [(ngModel)]="kavenegarForm.priority" /></ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.isDefault')"><p-checkbox [(ngModel)]="kavenegarForm.isDefault" [binary]="true" /></ain-form-field>
          </div>
          @if (webhookSetup()) {
            <section class="webhook-copy">
              <h4>{{ t('notifications.providers.sections.webhookSetup') }}</h4>
              <ain-form-field [label]="t('notifications.providers.fields.dlrUrl')"><input pInputText [readonly]="true" [ngModel]="webhookSetup()!.dlrCallbackUrl" /></ain-form-field>
              <ain-form-field [label]="t('notifications.providers.fields.inboundUrl')"><input pInputText [readonly]="true" [ngModel]="webhookSetup()!.inboundCallbackUrl" /></ain-form-field>
              <ain-form-field [label]="t('notifications.providers.fields.callbackTokenStatus')"><input pInputText [readonly]="true" [ngModel]="webhookSetup()!.callbackVerificationTokenMask" /></ain-form-field>
              @if (callbackTokenReveal()) {
                <div class="token-reveal">
                  <ain-info-hint [titleKey]="'notifications.providers.callbackToken.warning.title'" [descriptionKey]="'notifications.providers.callbackToken.warning.description'" />
                  <ain-form-field [label]="t('notifications.providers.fields.callbackToken')"><input pInputText [readonly]="true" [ngModel]="callbackTokenReveal()" /></ain-form-field>
                  <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton type="button" severity="secondary" [label]="t('notifications.providers.actions.copyToken')" (click)="copyCallbackToken()"></button>
                </div>
              }
              @if (selectedId()) {
                <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton type="button" severity="warn" [label]="t('notifications.providers.actions.regenerateToken')" (click)="regenerateCallbackToken()"></button>
              }
            </section>
          }
          <ain-form-field [label]="t('notifications.providers.fields.testRecipient')"><input pInputText [(ngModel)]="testRecipient" /></ain-form-field>
          <div class="dialog-actions">
            <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton type="button" [label]="t('common.save')" [loading]="saving()" (click)="saveKavenegar()"></button>
            <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton type="button" severity="secondary" [label]="t('notifications.providers.actions.testConnection')" [loading]="testing()" (click)="testKavenegarConnection()"></button>
            <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton type="button" severity="help" [label]="t('notifications.providers.actions.testSend')" [loading]="testing()" (click)="testKavenegarSend()"></button>
          </div>
        } @else {
          <div class="grid">
            <ain-form-field [label]="t('notifications.providers.fields.displayName')"><input pInputText [(ngModel)]="smtpForm.displayName" /></ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.host')"><input pInputText [(ngModel)]="smtpForm.host" /></ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.port')"><p-inputNumber [(ngModel)]="smtpForm.port" /></ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.securityMode')">
              <p-select [options]="securityModeOptions" optionLabel="label" optionValue="value" [(ngModel)]="smtpForm.securityMode" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.username')"><input pInputText [(ngModel)]="smtpForm.username" /></ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.password')"><input pInputText type="password" [(ngModel)]="smtpForm.password" [placeholder]="t('notifications.providers.placeholders.secretMasked')" /></ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.fromEmail')"><input pInputText [(ngModel)]="smtpForm.fromEmail" /></ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.replyToEmail')"><input pInputText [(ngModel)]="smtpForm.replyToEmail" /></ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.priority')"><p-inputNumber [(ngModel)]="smtpForm.priority" /></ain-form-field>
            <ain-form-field [label]="t('notifications.providers.fields.isDefault')"><p-checkbox [(ngModel)]="smtpForm.isDefault" [binary]="true" /></ain-form-field>
          </div>
          <ain-form-field [label]="t('notifications.providers.fields.testRecipient')"><input pInputText [(ngModel)]="testRecipient" /></ain-form-field>
          <div class="dialog-actions">
            <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton type="button" [label]="t('common.save')" [loading]="saving()" (click)="saveSmtp()"></button>
            @if (editorMode() === 'smtp-edit') {
              <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton type="button" severity="secondary" [label]="t('notifications.providers.actions.testConnection')" [loading]="testing()" (click)="testSmtpConnection()"></button>
              <button ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" pButton type="button" severity="help" [label]="t('notifications.providers.actions.testSend')" [loading]="testing()" (click)="testSmtpSend()"></button>
            }
          </div>
        }
        @if (selectedId()) {
          <section ainCanAccess [ainCanAccessPermission]="'notifications.providers.manage-settings'" class="limit-editor">
            <ain-info-hint [titleKey]="'notifications.providers.help.limits.title'" [descriptionKey]="'notifications.providers.help.limits.description'" />
            <ain-form-field [label]="t('notifications.providers.fields.rateLimit')">
              <p-inputNumber [(ngModel)]="rateLimitPerMinute" [min]="1" [max]="100000" />
            </ain-form-field>
            <button pButton type="button" severity="secondary" [label]="t('notifications.providers.actions.saveRateLimit')" (click)="saveRateLimit()"></button>
          </section>
        }
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border:1px solid #e5e7eb; border-radius:12px; padding:12px; margin-bottom:12px; }
    .toolbar { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
    .grid { display:grid; gap:10px; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); }
    .actions { display:flex; gap:6px; flex-wrap:wrap; }
    .dialog-actions { display:flex; gap:8px; flex-wrap:wrap; margin-top:12px; }
    .webhook-copy { margin-top:12px; padding-top:8px; border-top:1px solid #e5e7eb; }
    .live-badge { font-size:0.85rem; }
    .limit-editor { border-top:1px solid #e5e7eb; margin-top:12px; padding-top:12px; }
    .dialog-back { margin-bottom:8px; }
  `]
})
export class NotificationsProvidersComponent implements OnInit {
  private readonly api = inject(NotificationService);
  private readonly i18n = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly messages = inject(MessageService);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly providers = signal<ChannelProviderConfigSummary[]>([]);
  readonly dialogVisible = signal(false);
  readonly editorMode = signal<ProviderEditorMode>(null);
  readonly selectedId = signal<string | null>(null);
  readonly saving = signal(false);
  readonly testing = signal(false);
  readonly gmailConnecting = signal(false);
  readonly webhookSetup = signal<KavenegarWebhookSetup | null>(null);
  readonly callbackTokenReveal = signal<string | null>(null);

  testRecipient = '';
  rateLimitPerMinute = 60;
  smtpForm = { displayName: '', host: '', port: 587, securityMode: 'StartTls', username: '', password: '', fromEmail: '', replyToEmail: '', priority: 100, isDefault: false, isEnabled: true };
  kavenegarForm = { displayName: '', senderLine: '', apiBaseUrl: 'https://api.kavenegar.com', apiKey: '', priority: 100, isDefault: false, isEnabled: true };

  readonly securityModeOptions = [
    { label: 'None', value: 'None' },
    { label: 'STARTTLS', value: 'StartTls' },
    { label: 'SSL', value: 'SslOnConnect' }
  ];

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.notificationProvidersChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(false));
    void this.setupLiveSubscription();
    this.handleGmailCallbackState();
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const providerId = params.get('providerId');
      if (!providerId) {
        this.callbackTokenReveal.set(null);
        this.dialogVisible.set(false);
        this.selectedId.set(null);
        return;
      }
      const provider = this.providers().find(item => item.id === providerId);
      if (provider && this.selectedId() !== providerId) this.openEdit(provider);
    });
    this.load(true);
  }

  t(key: string): string { return this.i18n.translate(key); }
  channelLabel(channel: unknown): string { return this.t(`notifications.enums.channel.${normalizeNotificationChannel(channel as never)}`); }
  providerLabel(key: string): string { return providerKeyLabel(key); }

  dialogTitle(): string {
    const mode = this.editorMode();
    if (mode === 'gmail') return this.t('notifications.providers.dialog.gmail');
    if (mode?.startsWith('kavenegar')) return this.t('notifications.providers.dialog.kavenegar');
    return this.t('notifications.providers.dialog.smtp');
  }

  onDialogVisibleChange(visible: boolean): void {
    this.dialogVisible.set(visible);
    if (!visible) this.callbackTokenReveal.set(null);
    if (!visible && this.route.snapshot.paramMap.has('providerId')) {
      this.selectedId.set(null);
      void this.router.navigate(['/notifications/providers'], { replaceUrl: true });
    }
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.api.listProviderConfigs().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: items => {
        this.providers.set(items);
        const providerId = this.route.snapshot.paramMap.get('providerId');
        const provider = providerId ? items.find(item => item.id === providerId) : null;
        if (provider && this.selectedId() !== provider.id) this.openEdit(provider);
      },
      error: () => this.error.set(this.t('common.error'))
    });
  }

  openSmtpCreate(): void {
    if (!this.canManageProviders()) return;
    this.callbackTokenReveal.set(null);
    this.editorMode.set('smtp-create');
    this.selectedId.set(null);
    this.smtpForm = { displayName: '', host: '', port: 587, securityMode: 'StartTls', username: '', password: '', fromEmail: '', replyToEmail: '', priority: 100, isDefault: false, isEnabled: true };
    this.dialogVisible.set(true);
  }

  openKavenegarCreate(): void {
    if (!this.canManageProviders()) return;
    this.callbackTokenReveal.set(null);
    this.editorMode.set('kavenegar-create');
    this.selectedId.set(null);
    this.kavenegarForm = { displayName: '', senderLine: '', apiBaseUrl: 'https://api.kavenegar.com', apiKey: '', priority: 100, isDefault: false, isEnabled: true };
    this.webhookSetup.set(null);
    this.dialogVisible.set(true);
  }

  openEdit(row: ChannelProviderConfigSummary): void {
    this.callbackTokenReveal.set(null);
    if (this.route.snapshot.paramMap.get('providerId') !== row.id) {
      void this.router.navigate(['/notifications/providers', row.id], { replaceUrl: true });
    }
    this.selectedId.set(row.id);
    this.api.getProviderConfig(row.id).subscribe({
      next: detail => {
        this.rateLimitPerMinute = detail.rateLimitPerMinute;
        if (detail.providerKey === 'gmail-oauth') {
          this.editorMode.set('gmail');
          this.dialogVisible.set(true);
          return;
        }
        if (detail.providerKey === 'kavenegar') {
          this.editorMode.set('kavenegar-edit');
          this.kavenegarForm = {
            displayName: detail.displayName,
            senderLine: detail.kavenegarSettings?.senderLine ?? '',
            apiBaseUrl: detail.kavenegarSettings?.apiBaseUrl ?? 'https://api.kavenegar.com',
            apiKey: '',
            priority: detail.priority,
            isDefault: detail.isDefault,
            isEnabled: detail.isEnabled
          };
          this.webhookSetup.set(detail.webhookSetup);
          this.dialogVisible.set(true);
          return;
        }
        this.editorMode.set('smtp-edit');
        this.smtpForm = {
          displayName: detail.displayName,
          host: detail.smtpSettings?.host ?? '',
          port: detail.smtpSettings?.port ?? 587,
          securityMode: String(detail.smtpSettings?.securityMode ?? 'StartTls'),
          username: detail.smtpSettings?.username ?? '',
          password: '',
          fromEmail: detail.smtpSettings?.fromEmail ?? '',
          replyToEmail: detail.smtpSettings?.replyToEmail ?? '',
          priority: detail.priority,
          isDefault: detail.isDefault,
          isEnabled: detail.isEnabled
        };
        this.dialogVisible.set(true);
      }
    });
  }

  saveSmtp(): void {
    if (!this.canManageProviders()) return;
    const validation = validateSmtpSettings({ host: this.smtpForm.host, port: this.smtpForm.port, fromEmail: this.smtpForm.fromEmail });
    if (validation) return;
    this.saving.set(true);
    const payload = {
      accountKey: null,
      displayName: this.smtpForm.displayName,
      priority: this.smtpForm.priority,
      isDefault: this.smtpForm.isDefault,
      isEnabled: this.smtpForm.isEnabled,
      settings: {
        host: this.smtpForm.host,
        port: this.smtpForm.port,
        securityMode: this.smtpForm.securityMode,
        useAuthentication: Boolean(this.smtpForm.username),
        username: this.smtpForm.username || null,
        fromEmail: this.smtpForm.fromEmail,
        fromDisplayName: null,
        replyToEmail: this.smtpForm.replyToEmail || null,
        timeoutSeconds: 30
      },
      password: this.smtpForm.password || null,
      clearPassword: false
    };
    const req = this.editorMode() === 'smtp-edit' && this.selectedId()
      ? this.api.updateSmtpProvider(this.selectedId()!, payload)
      : this.api.createSmtpProvider(payload);
    req.pipe(finalize(() => this.saving.set(false))).subscribe({
      next: () => { this.dialogVisible.set(false); this.load(false); }
    });
  }

  saveKavenegar(): void {
    if (!this.canManageProviders()) return;
    this.saving.set(true);
    const payload = {
      accountKey: null,
      displayName: this.kavenegarForm.displayName,
      priority: this.kavenegarForm.priority,
      isDefault: this.kavenegarForm.isDefault,
      isEnabled: this.kavenegarForm.isEnabled,
      settings: {
        senderLine: this.kavenegarForm.senderLine,
        sendingMode: 'Plain',
        apiBaseUrl: this.kavenegarForm.apiBaseUrl,
        verifyTemplateName: null,
        allowedCallbackIpAddresses: null,
        enablePollingReconciliation: true
      },
      apiKey: this.kavenegarForm.apiKey || null,
      clearApiKey: false,
      callbackVerificationToken: null,
      regenerateCallbackToken: false
    };
    const req = this.editorMode() === 'kavenegar-edit' && this.selectedId()
      ? this.api.updateKavenegarProvider(this.selectedId()!, payload)
      : this.api.createKavenegarProvider(payload);
    req.pipe(finalize(() => this.saving.set(false))).subscribe({
      next: (detail: ChannelProviderConfigDetail) => {
        this.webhookSetup.set(detail.webhookSetup);
        const revealedToken = detail.webhookSetup?.callbackVerificationToken ?? null;
        this.callbackTokenReveal.set(revealedToken);
        if (revealedToken) {
          this.selectedId.set(detail.id);
          this.editorMode.set('kavenegar-edit');
          void this.router.navigate(['/notifications/providers', detail.id], { replaceUrl: true });
        } else {
          this.onDialogVisibleChange(false);
        }
        this.load(false);
      }
    });
  }

  connectGmail(): void {
    if (!this.canManageProviders()) return;
    this.gmailConnecting.set(true);
    this.api.connectGmail('/notifications/providers').pipe(finalize(() => this.gmailConnecting.set(false))).subscribe({
      next: result => { window.location.href = result.authorizationUrl; }
    });
  }

  disconnectGmail(): void {
    if (!this.canManageProviders()) return;
    const id = this.selectedId();
    if (!id) return;
    this.api.disconnectGmail(id).subscribe(() => { this.dialogVisible.set(false); this.load(false); });
  }

  toggleEnabled(row: ChannelProviderConfigSummary): void {
    if (!this.canManageProviders()) return;
    const req = row.isEnabled ? this.api.disableProvider(row.id) : this.api.enableProvider(row.id);
    req.subscribe(() => this.load(false));
  }

  testSmtpConnection(): void {
    if (!this.canManageProviders()) return;
    const id = this.selectedId();
    if (!id) return;
    this.testing.set(true);
    this.api.testSmtpConnection(id).pipe(finalize(() => this.testing.set(false))).subscribe();
  }

  testSmtpSend(): void {
    if (!this.canManageProviders()) return;
    const id = this.selectedId();
    if (!id || validateTestRecipient(this.testRecipient)) return;
    this.testing.set(true);
    this.api.testSmtpSend(id, this.testRecipient.trim()).pipe(finalize(() => this.testing.set(false))).subscribe();
  }

  testGmailSend(): void {
    if (!this.canManageProviders()) return;
    const id = this.selectedId();
    if (!id || validateTestRecipient(this.testRecipient)) return;
    this.testing.set(true);
    this.api.testGmailSend(id, this.testRecipient.trim()).pipe(finalize(() => this.testing.set(false))).subscribe();
  }

  testKavenegarConnection(): void {
    if (!this.canManageProviders()) return;
    const id = this.selectedId();
    if (!id) return;
    this.testing.set(true);
    this.api.testKavenegarConnection(id).pipe(finalize(() => this.testing.set(false))).subscribe();
  }

  testKavenegarSend(): void {
    if (!this.canManageProviders()) return;
    const id = this.selectedId();
    if (!id || validateTestRecipient(this.testRecipient)) return;
    this.testing.set(true);
    this.api.testKavenegarSend(id, this.testRecipient.trim(), this.t('notifications.providers.testMessage')).pipe(finalize(() => this.testing.set(false))).subscribe();
  }

  saveRateLimit(): void {
    if (!this.canManageProviders()) return;
    const id = this.selectedId();
    if (!id || this.rateLimitPerMinute < 1) return;
    this.api.updateProviderRateLimit(id, this.rateLimitPerMinute).subscribe(() => this.load(false));
  }

  regenerateCallbackToken(): void {
    if (!this.canManageProviders()) return;
    const id = this.selectedId();
    if (!id) return;
    this.saving.set(true);
    this.api.regenerateKavenegarCallbackToken(id)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe(result => {
        this.webhookSetup.set(result.webhookSetup);
        this.callbackTokenReveal.set(result.callbackVerificationToken);
      });
  }

  copyCallbackToken(): void {
    const token = this.callbackTokenReveal();
    if (!token) return;
    void navigator.clipboard.writeText(token);
  }

  private handleGmailCallbackState(): void {
    const status = this.route.snapshot.queryParamMap.get('gmailStatus');
    if (!status) return;
    const success = status === 'success';
    this.messages.add({
      severity: success ? 'success' : 'error',
      summary: this.t(success ? 'notifications.providers.gmail.success' : 'notifications.providers.gmail.error'),
      detail: success
        ? this.t('notifications.providers.gmail.connected')
        : this.t(this.gmailErrorKey(this.route.snapshot.queryParamMap.get('errorCode')))
    });
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { gmailStatus: null, providerId: null, errorCode: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private gmailErrorKey(errorCode: string | null): string {
    if (errorCode?.startsWith('gmail_oauth_denied:')) return 'notifications.providers.gmail.errors.denied';
    const known = new Set([
      'gmail_oauth_failed',
      'gmail_oauth_invalid_callback',
      'gmail_oauth_state_invalid',
      'gmail_oauth_state_expired',
      'gmail_oauth_state_replayed'
    ]);
    return `notifications.providers.gmail.errors.${errorCode && known.has(errorCode) ? errorCode : 'unknown'}`;
  }

  private canManageProviders(): boolean {
    const context = this.context.context();
    return (context?.roles ?? []).includes('SystemAdmin') ||
      (context?.permissions ?? []).some(item => item.key === 'notifications.providers.manage-settings');
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) return;
    await this.liveUpdateService.subscribe(notificationProvidersTopic(tenantId));
  }
}
